import { SyntheticEvent, useEffect, useRef, useState } from "react";
import { updateThreadTitle } from "duck/endpoints/threads/api";
import { useThreadStreaming } from "duck/ui/chat/hooks";
import { subthreadManager } from "duck/ui/chat/subthreadManager";
import { ChatMessage } from "duck/ui/chat/types";
import { useTranslation } from "react-i18next";
import useLocalStorageState from "use-local-storage-state";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import Box from "@mui/material/Box";

import Accordion from "features/components/Accordion";
import Typography from "features/components/Typography";

import SubthreadMessages from "./SubthreadMessages";
import SubthreadStatusIndicator, {
  SubthreadStatus,
} from "./SubthreadStatusIndicator";
import {
  getInitialStatus,
  hasAiMessagesWithContent,
  parseSubthreadContent,
} from "./utils";

interface Props {
  message: ChatMessage;
}

const SubthreadMessage = ({ message }: Props) => {
  const { t } = useTranslation("duck");
  const subthreadData = parseSubthreadContent(message.content);
  const threadId = subthreadData?.thread_id;

  const [status, setStatus] = useState<SubthreadStatus>(() =>
    getInitialStatus(threadId)
  );
  const [isExpanded, setIsExpanded] = useLocalStorageState(
    `subthread-expanded-${threadId}`,
    { defaultValue: false }
  );

  const {
    messages: fetchedMessages,
    title: threadTitle,
    mutateThread,
  } = useThreadStreaming({
    threadId: threadId ?? "",
    skipRequest: !threadId,
  });

  // use thread title from API, fallback to subthreadData title (from parent's tool_response)
  const title = threadTitle || subthreadData?.thread_title || "";

  // update subthreadManager when messages are fetched from API
  useEffect(() => {
    if (threadId && fetchedMessages.length > 0) {
      const currentState = subthreadManager.getState(threadId);

      // SWR can revalidate during streaming (e.g. network reconnect, component remount),
      // the API response may lag behind SSE, so only use it when not actively streaming
      if (!currentState.isStreaming) {
        subthreadManager.updateMessages(threadId, fetchedMessages);
      }
    }
  }, [threadId, fetchedMessages]);

  const wasStreamingRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!threadId) return;

    // reset ref when threadId changes
    wasStreamingRef.current = null;

    const unsubscribe = subthreadManager.subscribe(threadId, (state) => {
      const wasStreaming = wasStreamingRef.current;
      wasStreamingRef.current = state.isStreaming;

      if (state.isStreaming) {
        if (hasAiMessagesWithContent(state.messages)) {
          setStatus(SubthreadStatus.PARTIALLY_COMPLETED);
        } else {
          setStatus(SubthreadStatus.STREAMING);
        }
      } else if (state.messages.length > 0) {
        setStatus(SubthreadStatus.COMPLETED);

        // call update_title when streaming ends, then refetch thread metadata
        if (wasStreaming === true) {
          updateThreadTitle(threadId)
            .then(() => {
              mutateThread();
            })
            .catch((error) => {
              console.error("Failed to update subthread title:", error);
            });
        }
      } else {
        setStatus(SubthreadStatus.STREAMING);
      }
    });

    return unsubscribe;
  }, [threadId, mutateThread]);

  const handleChange = (_event: SyntheticEvent, expanded: boolean) => {
    setIsExpanded(expanded);
  };

  const statusText: Record<SubthreadStatus, string> = {
    [SubthreadStatus.STREAMING]: t("subthread_status_working"),
    [SubthreadStatus.PARTIALLY_COMPLETED]: t(
      "subthread_status_partially_completed"
    ),
    [SubthreadStatus.COMPLETED]: t("subthread_status_completed"),
  };

  if (!subthreadData || !threadId) {
    return null;
  }

  const titleElement = (
    <Typography
      variant="overline"
      className="!text-gray-400 flex items-center gap-2 overflow-hidden"
    >
      <AccountTreeOutlinedIcon fontSize="inherit" />
      <SubthreadStatusIndicator
        status={status}
        title={t("subthread_prefix", { status: statusText[status] })}
      />
      <span className="truncate" title={title}>
        {title}
      </span>
    </Typography>
  );

  return (
    <Box data-testid="subthread-message" className="w-full">
      <Accordion
        id={threadId}
        title={titleElement}
        expanded={isExpanded}
        onChange={handleChange}
        size="small"
        className="!bg-slate-20"
      >
        <SubthreadMessages threadId={threadId} />
      </Accordion>
    </Box>
  );
};

export default SubthreadMessage;
